import type { ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';

import { log } from '../../utils/monitoring';
import { APP_TOKEN } from '../constants';
import { LOGIN_BAR_CONTROLLER_TYPE } from '../constants/layouts/login-bar/classic-login-bar-layouts';
import { connectToController } from '../wrappers/controllers';

const LOGIN_BAR_COMP_TYPE = 'wysiwyg.viewer.components.LoginSocialBar';

const maybeReconnectLoginBar = async (editorSDK: EditorSDK, controllerRef: ComponentRef) => {
  const [loginBarRef] = await editorSDK.components.getChildren(APP_TOKEN, { componentRef: controllerRef });

  if (!loginBarRef) {
    log('Login bar controller has no child component', { extra: { controllerId: controllerRef.id } });
    return false;
  }

  const componentType = await editorSDK.components.getType(APP_TOKEN, { componentRef: loginBarRef });

  if (componentType !== LOGIN_BAR_COMP_TYPE) {
    return false;
  }

  const connections = await editorSDK.controllers.listConnections(APP_TOKEN, { componentRef: loginBarRef });

  if (connections.length > 0) {
    return false;
  }

  await connectToController({
    editorSDK,
    connectToRef: loginBarRef,
    controllerRef,
    role: LOGIN_BAR_CONTROLLER_TYPE,
    connectionConfig: { isMembersAreaV2: true },
    isPrimary: true,
  });

  log('Reconnected disconnected login bar to its widget controller', {
    extra: { controllerId: controllerRef.id, loginBarId: loginBarRef.id },
  });

  return true;
};

export const maybeFixDisconnectedLoginBars = async (editorSDK: EditorSDK) => {
  try {
    const loginBarControllers = await (editorSDK as any).document.controllers.findAllByType(APP_TOKEN, {
      controllerType: LOGIN_BAR_CONTROLLER_TYPE,
    });

    let reconnectedCount = 0;
    for (const controller of loginBarControllers ?? []) {
      const controllerRef = controller?.controllerRef as ComponentRef | undefined;
      if (!controllerRef) {
        continue;
      }
      const reconnected = await maybeReconnectLoginBar(editorSDK, controllerRef);
      if (reconnected) {
        reconnectedCount++;
      }
    }

    if (reconnectedCount > 0) {
      await editorSDK.document.application.livePreview.refresh(APP_TOKEN, {
        shouldFetchData: false,
        source: 'LOGIN_BAR_FIXER',
      });
    }
  } catch (e) {
    log('An error was thrown while fixing disconnected login bars', {
      extra: { error: e },
    });
  }
};
